<template>
  <transition name="modal">
    <div class="overlay-bg">
      <div class="overlay">
        <div class="loading-wrapper" v-if="isLoading">
          <LoadingAnimation />
        </div>

        <button class="overlay__close-btn" @click="$emit('close-panel')">
          <span class="overlay__close-btn-text">Close</span>
        </button>

        <div class="container">
          <h1 data-testid="save-package-title">Save Your Package</h1>

          <form
            class="form form--email"
            novalidate
            autocomplete="on"
            @submit.prevent="formSubmit"
          >
            <WarningPanel
              v-if="errorResponse"
              :msg="errorResponse"
              severe="true"
            />

            <fieldset class="form__fieldset" v-if="!emailSent">
              <div class="form-item">
                <label class="form__label" for="email">
                  <span class="email-label"
                    >Pop in your email address and we’ll send you a link so you
                    can come back to your selected package later</span
                  >
                  <FormError
                    v-if="$v.email.$error && !$v.email.required"
                    data-testid="email-required-error"
                    msg="Email address required"
                  />
                  <FormError
                    v-if="$v.email.$error && !$v.email.email"
                    data-testid="email-invalid-error"
                    msg="Valid email address required"
                  />
                  <FormError
                    v-if="errorResponse"
                    data-testid="email-sending-error"
                    msg="There has been a problem sending the email, try again or note down share link for reference"
                  />
                </label>

                <div class="input-wrapper">
                  <input
                    type="text"
                    name="email"
                    id="email"
                    class="form__input"
                    placeholder="Email"
                    required
                    autocomplete="email"
                    v-model.trim="email"
                    data-testid="package-share-email"
                  />
                  <button type="submit" class="btn btn--big">Save</button>
                </div>
              </div>
            </fieldset>
            <div class="form-success" v-else>
              <EnvelopeIcon
                width="50px"
                height="38px"
                aria-role="presentation"
              />
              <h4>Saved!</h4>
              <p>
                We’ve saved your package for you and emailed you a link so you
                can come back to it whenever you’re ready. You can also use this
                link to share it with your housemates.
              </p>
            </div>
          </form>
        </div>

        <div v-if="shareLink" class="container package-share-wrapper">
          <h1>Share Your Package With Your Housemates</h1>

          <SocialShare
            :address-line1="addressLine1"
            :share-link="shareLink"
            :share-subject="shareSubject"
            native-share-title="Share Now"
            :share-body="shareBody"
            :whats-app-share-body="whatsAppShareBody"
            :facebook-title="facebookTitle"
            :facebook-description="facebookDescription"
            :facebook-image-url="facebookImageUrl"
            :inline="shouldInlineShare"
            @whatsAppShare="trackWhatsAppShare"
            @facebookShare="trackFacebookShare"
            @packageShare="trackEmailShare"
            @copyShareLink="trackCopyLink"
            @nativeShare="trackNativeShare"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { email, required } from "vuelidate/lib/validators";
import axiosInstance from "@/utility/axiosInstance";
import FormError from "@/components/forms/FormError";
import SocialShare from "@/components/SocialShare";
import WarningPanel from "@/components/WarningPanel";
import { isSmallDevice } from "@/utility/matchMedia";

import EnvelopeIcon from "@/assets/svg/icon-envelope.svg";
import LoadingAnimation from "@/components/LoadingAnimation";

export default {
  name: "PackageSaveShareOverlay",
  components: {
    LoadingAnimation,
    EnvelopeIcon,
    FormError,
    WarningPanel,
    SocialShare,
  },
  data() {
    return {
      isLoading: false,
      shareLink: null,
      emailSent: false,
      errorResponse: "",
      shareSubject: `Split The Bills join link for our utilities at `,
      shareBody: `Hey,\n\nLet’s not faff about or fuss over our shared utilities. Split The Bills will set up and pay them for us. We just make one monthly payment each from our own bank accounts. Easy! Check out our bills package here:\n\n`,
      whatsAppShareBody:
        "Check out the bills package I’ve made with Split The Bills. They’ll set up and pay our shared bills:\n\n",
      facebookTitle: "Split the Bills Quote",
      facebookDescription:
        "Hi, I've found a great deal for our utilities and broadband, check it out...",
    };
  },
  computed: {
    ...mapState(["referral", "products", "discount"]),
    ...mapState({
      addressGuid: (state) => {
        return state.houseInformation.address.guid;
      },
    }),
    ...mapGetters([
      "addressLine1",
      "postcode",
      "housemateCount",
      "selectedProducts",
      "hasValidDiscount",
      "hasUsedDiscount",
      "hasAddressGuid",
    ]),
    email: {
      get() {
        return this.$store.state.personalDetails.email;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { email: value });
      },
    },
    facebookImageUrl() {
      return `${document.location.origin}${
        process.env.BASE_URL
      }share-generic.png`;
    },
    shouldInlineShare() {
      return !isSmallDevice();
    },
    selectedProductIds() {
      return this.selectedProducts.map((product) => product.priceId);
    },
  },
  methods: {
    trackWhatsAppShare() {
      this.trackGoogleEvent(
        "Click share via WhatsApp",
        "Get Price",
        "Share price WhatsApp",
      );
      this.trackFacebookEvent("Click share via WhatsApp");
    },
    trackFacebookShare() {
      this.trackGoogleEvent(
        "Click share via Facebook",
        "Get Price",
        "Share price Facebook",
      );
      this.trackFacebookEvent("Click share via Facebook");
    },
    trackEmailShare() {
      this.trackGoogleEvent(
        "Click share via email",
        "Get Price",
        "Share price email",
      );
      this.trackFacebookEvent("Click share via email");
    },
    trackCopyLink() {
      this.trackGoogleEvent("Click Copy", "Get Price", "Copy share price link");
      this.trackFacebookEvent("Click Copy");
    },
    trackNativeShare() {
      this.trackGoogleEvent(
        "Share Package Link",
        "Get Price",
        "Native Link Share",
      );
      this.trackFacebookEvent("Native Package Link Share");
    },
    formSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.$store.commit("SHOW_LOADING_PANEL");
        this.errorResponse = "";

        this.trackGoogleEvent(
          "Save Package with Email Address",
          "Get Price",
          "Save Package with Email Address",
        );
        this.trackFacebookEvent("Save Package with Email Address");

        const shareData = {
          email: this.email,
          hm8s: this.housemateCount,
          selectedProducts: this.selectedProductIds,
          shareLink: this.shareLink,
        };

        if (this.hasAddressGuid) {
          shareData["addressGuid"] = this.addressGuid;
        } else {
          shareData["postcode"] = this.postcode;
        }

        if (this.referral.isValid) {
          shareData.referral = this.referral.code;
        }

        if (this.hasUsedDiscount) {
          shareData.offerCode = this.discount.offer;
          shareData.affiliateCode = this.discount.aff;
        }

        if (this.$route.query.packageShare) {
          shareData.packageShare = this.$route.query.packageShare;
        }

        if (this.hasValidDiscount) {
          shareData.voucherCode = this.discount.code;
        }

        axiosInstance
          .post("/email_summary", shareData)
          .then(() => {
            this.emailSent = true;
          })
          .catch((error) => {
            this.emailSent = false;

            if (!error.response) {
              this.errorResponse =
                "Network error accessing the Split The Bills API";
            } else {
              this.errorResponse = error.response.data.messages[0];
            }
          })
          .then(() => {
            this.$store.commit("HIDE_LOADING_PANEL");
          });
      }
    },
    fetchSharePackageLink() {
      this.isLoading = true;

      const shareData = {
        hm8s: this.housemateCount,
        selectedProducts: this.selectedProductIds,
      };

      if (this.hasAddressGuid) {
        shareData["addressGuid"] = this.addressGuid;
      } else {
        shareData["postcode"] = this.postcode;
      }

      if (this.referral.isValid) {
        shareData.referral = this.referral.code;
      }

      if (this.hasValidDiscount) {
        shareData.voucherCode = this.discount.code;
      }

      if (this.email) {
        shareData.fetchEmail = this.email;
      }

      if (this.$route.query.packageShare) {
        shareData.packageShare = this.$route.query.packageShare;
      }

      axiosInstance
        .post("/share_package", shareData)
        .then(({ data }) => {
          this.shareLink = data;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  created() {
    this.fetchSharePackageLink();
  },
  validations() {
    return {
      email: {
        required,
        email,
      },
    };
  },
};
</script>

<style scoped lang="scss">
.container {
  padding: 0;
}

h1 {
  margin-bottom: 1rem;
  font-size: $button-font-size;
}

h3 {
  font-size: 2rem;
  font-weight: $font-regular;
  color: $black;
  margin: 0 0 $spacing-medium;
}

.form--email {
  margin-bottom: $spacing-large;

  .input-wrapper {
    border: 2px solid $grey-mid;
    border-radius: $border-radius-small;

    @include media("<medium") {
      border: none;
    }

    @include media(">=medium") {
      @include inlineform;
    }

    input {
      background: none;
      flex-shrink: 1;

      @include media(">=medium") {
        border: none;
        margin: 0 $spacing-small;
        padding: 0;
      }

      &:focus {
        outline: none;
      }
    }

    button {
      flex-shrink: 0;
      background: $brand-green;
      color: $white;
      padding: $spacing-tiny 0;
      margin: $spacing-tiny $spacing-tiny $spacing-tiny 0;
      width: 18%;
      border: none;

      @include media("<medium") {
        width: 100%;
        margin-top: $spacing-xs;
      }

      @include media(">=medium") {
        min-height: unset;
      }

      &:focus {
        outline: none;
      }
    }
  }
}

.form-success {
  text-align: center;
  color: $white;
  background-color: $brand-green;
  padding: $spacing-medium;
  border-radius: $border-radius-small;

  svg {
    width: 50px;
    height: 38px;
    margin: $spacing-small auto $spacing-medium;
  }

  h4 {
    font-weight: $font-regular;
    margin: 0 0 $spacing-small;
  }

  p {
    font-size: 1.6rem;
    margin: 0;
  }
}

.email-label {
  font-size: 18px;
  display: block;
  margin-bottom: 15px;
  text-align: center;
}

.package-share-wrapper {
  h1 {
    margin-bottom: 0;

    @include media("<medium") {
      margin-bottom: $spacing-medium;
    }
  }
}

.loading-wrapper {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: $brand-pink;

  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
